import { ILogger } from "./ILogger"
import { registerPlugin } from "./utils/registerPlugin"

export type CardTransactionData = {
  uti: string
  amountTrans: number
  amountGratuity: number
  amountCashback: number
  transApproved: boolean
  transCancelled: boolean
  cvmSigRequired: boolean
  cvmPinVerified: boolean
  voiceAuthorized: boolean | null
  transCurrencyCode: string
  terminalId: string | null
  merchantId: string | null
  softwareVersion: string
  receiptNumber: number
  retrievalReferenceNumber: string | null
  responseCode: string | null
  stan: string | null
  authorisationCode: string
  merchantTokenId: string | null
  cardPan: string | null
  cardExpiryDate: string | null
  cardStartDate: string | null
  cardScheme: string | null
  cardPanSequenceNumber: string | null
  cardType: string | null
  emvAid: string | null
  emvTsi: string | null
  emvTvr: string | null
  emvCardholderName: string | null
  emvApplicationCryptogram: string | null
  emvApplicationCryptogramType: "AAC" | "ARQC" | "TC" | null
  serverMessage: string | null
  isDccTransaction: boolean
  transactionAmountInCardCurrency: number | null
  currencyExchangeRate: number | null
  cardCurrency: string | null
  dccTextFirst: string | null
  dccTextSecond: string | null
  dccMarkupValue: number | null
  dccMarkupText: string | null
  dccMarkupInfo: string | null
  dccWholesaleRatesText: string | null
  terminalPrintedReceipt: boolean | null
  cardholderReceipt: string | null
  merchantReceipt: string | null
}

type DevicePaymentRequest = {
  amountTrans: number
  amountGratuity: number
  disablePrinting: boolean
  cardholderNotPresent: boolean
  gratuityIsAvailable: boolean
}

type DeviceRefundRequest = {
  amountTrans: number
  disablePrinting: boolean
  retrievalReferenceNumber: string | null
  cardholderNotPresent: boolean
  dccCardCurrencyAmount?: number
  dccCardholderCurrency?: string
  dccExchangeRate?: number
}

type DeviceTransactionResponse = {
  data: CardTransactionData
  events: Array<string>
}
export type DeviceVoidStatus =
  | "approved"
  | "cancelled"
  | "failed"
  | "not_possible"

type DeviceVoidTransactionResponse =
  | {
      status: "approved"
      data?: CardTransactionData
      events: Array<string>
    }
  | {
      status: Exclude<DeviceVoidStatus, "approved">
      data?: never
      events?: Array<string>
    }

type DeviceVoidRequest = {
  transactionAmount: number // No idea why this needs to be here but EvoPayments requires it on TR_CANCEL
  uti: string
  disablePrinting?: boolean
}

export type IPaymentPlugin = {
  makeDevicePayment: (
    request: DevicePaymentRequest
  ) => Promise<DeviceTransactionResponse>
  makeDeviceRefund: (
    request: DeviceRefundRequest
  ) => Promise<DeviceTransactionResponse>
  makeDeviceVoid: (
    request: DeviceVoidRequest
  ) => Promise<DeviceVoidTransactionResponse>
}

const rawPlugin: IPaymentPlugin =
  registerPlugin<IPaymentPlugin>("IPaymentPlugin", {
    makeDevicePayment: () => {
      throw new Error(
        "Card payment is not supported on web platform"
      )
    },
    makeDeviceRefund: () => {
      throw new Error(
        "Card refund is not supported on web platform"
      )
    },
    makeDeviceVoid: () => {
      throw new Error(
        "Card void is not supported on web platform"
      )
    },
  })

// Wrap the actual plugin to extract common errors handling behaviour
export const IPayment: IPaymentPlugin = {
  makeDevicePayment: async (request) => {
    try {
      ILogger.logToDetailLog({
        message: "Start makeDevicePayment",
      })

      if (import.meta.env.VITE_PLATFORM === "paxEvo") {
        const { level } = await (
          navigator as any
        ).getBattery()

        if (level < 0.15) {
          throw new Error("Battery too low")
        }
      }

      const response =
        await rawPlugin.makeDevicePayment(request)

      ILogger.logToDetailLog({
        message: "End makeDevicePayment",
      })

      return response
    } catch (error) {
      ILogger.logToDetailLog({
        message: `ERROR makeDevicePayment: ${error}`,
      })

      if (
        (error as Error).message ===
        "No response from positive"
      ) {
        toast.error("Unable to launch payment application")
      } else if (
        (error as Error).message ===
        "EvoPayments application is busy"
      ) {
        toast.error(
          "EvoPayments is busy. Exit TP+ and close EvoPayments to make payments.",
          10000
        )
      } else if (
        (error as Error).message === "Battery too low"
      ) {
        toast.info("Battery level too low", 5000)
      }

      throw error
    }
  },
  makeDeviceRefund: async (request) => {
    try {
      ILogger.logToDetailLog({
        message: "START makeDeviceRefund",
      })

      const response =
        await rawPlugin.makeDeviceRefund(request)

      ILogger.logToDetailLog({
        message: "END makeDeviceRefund",
      })

      return response
    } catch (error) {
      ILogger.logToDetailLog({
        message: `ERROR makeDeviceRefund: ${error}`,
      })

      if (
        (error as Error).message ===
        "No response from positive"
      ) {
        toast.error("Unable to launch payment application")
      } else if (
        (error as Error).message ===
        "EvoPayments application is busy"
      ) {
        toast.error(
          "EvoPayments is busy. Exit TP+, then open and close EvoPayments to make payments.",
          10000
        )
      }
      throw error
    }
  },
  makeDeviceVoid: async (request) => {
    try {
      ILogger.logToDetailLog({
        message: "START makeDeviceVoid",
      })

      const response =
        await rawPlugin.makeDeviceVoid(request)

      ILogger.logToDetailLog({
        message: "END makeDeviceVoid",
      })

      return response
    } catch (error) {
      ILogger.logToDetailLog({
        message: `ERROR makeDeviceVoid: ${error}`,
      })

      if (
        (error as Error).message ===
        "No response from positive"
      ) {
        toast.error("Unable to launch payment application")
      } else if (
        (error as Error).message ===
        "EvoPayments application is busy"
      ) {
        toast.error(
          "EvoPayments is busy. Exit TP+, then open close EvoPayments to make payments.",
          10000
        )
      }
      throw error
    }
  },
}
